/**
 * Pinochle bot.
 *
 * - Bidding: for each suit as trump it estimates the team's meld plus counters (its own meld, an average partner meld,
 *   counters in hand and trump length) and bids just above the standing bid while that stays under its ceiling.
 * - Passing: the partner sends the declarer its strongest cards that it does not need for its own meld; the declarer
 *   keeps the best twelve (meld first, then card values) and returns the rest.
 * - Play: partnership-aware. It feeds counters to a partner who is winning, takes counters with the cheapest card that
 *   wins, draws trumps when it holds the contract, and leads master cards (NORMAL and above track played cards).
 * The bot only ever reads its own seat's view.
 */
import { parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { pinochleMeld } from './meld.js';
import {
  pinochleBestPlay,
  pinochleBestSuit,
  pinochleCardValue,
  pinochleCounter,
  pinochleDeck,
  pinochleLegalCards,
  pinochlePartnerOf,
  pinochleStrength,
  pinochleTeamOf,
  type PinochleAction,
  type PinochleLegal,
  type PinochleTrickPlay,
  type PinochleView,
} from './engine.js';

/** Average meld a partner brings, and the share of the 25 counters a team takes before adjusting for its hand. */
const PARTNER_MELD = 6;
const BASE_COUNTERS = 12.5;

/** Expected team total (meld + counters) with `trump`, judged from a twelve-card hand. */
export function estimatePinochle(hand: readonly CardId[], trump: Suit): number {
  const meld = pinochleMeld(hand, trump).points;
  const counters = hand.reduce((n, c) => n + pinochleCounter(c), 0);
  const trumps = hand.filter((c) => parseCard(c).suit === trump);
  const topTrumps = trumps.filter((c) => pinochleStrength(c) >= 5).length;
  let est = meld + PARTNER_MELD + BASE_COUNTERS + (counters - 6) * 1.0;
  est += Math.max(0, trumps.length - 3) * 1.2 + topTrumps * 0.8;
  return est;
}

function bestTrump(hand: readonly CardId[]): { suit: Suit; est: number } {
  let suit = pinochleBestSuit(hand);
  let est = estimatePinochle(hand, suit);
  for (const s of SUITS) {
    const e = estimatePinochle(hand, s);
    if (e > est + 0.05) {
      est = e;
      suit = s;
    }
  }
  return { suit, est };
}

/** All ways to pick `k` items. */
function combos<T>(items: readonly T[], k: number): T[][] {
  const out: T[][] = [];
  const rec = (start: number, picked: T[]) => {
    if (picked.length === k) {
      out.push([...picked]);
      return;
    }
    for (let i = start; i < items.length; i++) {
      picked.push(items[i]!);
      rec(i + 1, picked);
      picked.pop();
    }
  };
  rec(0, []);
  return out;
}

function choosePass(view: PinochleView, role: 'partner' | 'declarer'): [CardId, CardId, CardId] {
  const trump = view.trump!;
  const hand = view.hand;
  const value = (cs: readonly CardId[]) => cs.reduce((n, c) => n + pinochleCardValue(c, trump), 0);
  let best: CardId[] = hand.slice(0, 3);
  let bestScore = -Infinity;
  for (const give of combos(hand, 3)) {
    const keep = hand.filter((c) => !give.includes(c));
    const meldKept = pinochleMeld(keep, trump).points;
    // The partner hands over its best cards (minus any meld it breaks); the declarer returns its worst.
    const score = role === 'partner' ? value(give) - 0.6 * (pinochleMeld(hand, trump).points - meldKept) : meldKept + 0.35 * value(keep);
    if (score > bestScore) {
      bestScore = score;
      best = give;
    }
  }
  return [best[0]!, best[1]!, best[2]!];
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: PinochleView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(pinochleDeck().filter((c) => !seen.has(c))) };
}

/** No unseen card of the suit is stronger (a tie goes to the card played first). */
function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) if (parseCard(u).suit === c.suit && pinochleStrength(u) > pinochleStrength(card)) return false;
  return true;
}

function chooseCard(view: PinochleView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const trump = view.trump!;
  const trick = view.trick;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  const val = (c: CardId) => pinochleCardValue(c, trump);
  const cheap = (cards: CardId[]) => [...cards].sort((a, b) => pinochleCounter(a) - pinochleCounter(b) || val(a) - val(b))[0]!;
  const isTrump = (c: CardId) => parseCard(c).suit === trump;
  const declarerTeam = view.declarer !== null && pinochleTeamOf(view.declarer) === pinochleTeamOf(me);

  if (trick.length === 0) {
    const trumps = legal.filter(isTrump);
    const side = legal.filter((c) => !isTrump(c));
    const masters = (cards: CardId[]) => (k ? cards.filter((c) => isMaster(c, k)) : cards.filter((c) => pinochleStrength(c) === 6));
    if (declarerTeam && trumps.length > 0 && (trumps.length >= 4 || masters(trumps).length > 0)) {
      const top = masters(trumps);
      if (top.length > 0) return [...top].sort((a, b) => pinochleStrength(b) - pinochleStrength(a))[0]!;
      return cheap(trumps);
    }
    const sideMasters = masters(side);
    if (sideMasters.length > 0) return [...sideMasters].sort((a, b) => pinochleStrength(b) - pinochleStrength(a))[0]!;
    return cheap(side.length > 0 ? side : legal);
  }

  const best = pinochleBestPlay(trick, trump);
  const partnerWinning = best.seat === pinochlePartnerOf(me);
  const last = trick.length === 3;
  const winners = legal.filter((c) => {
    const a = parseCard(c);
    const b = parseCard(best.card);
    return (a.suit === trump && b.suit !== trump) || (a.suit === b.suit && pinochleStrength(c) > pinochleStrength(best.card));
  });

  if (partnerWinning) {
    // Safe when nobody can still beat it: we are last, or it is the top card left and cannot be trumped.
    const trumpLeft = k !== null && [...k.unseen].some((u) => parseCard(u).suit === trump);
    const secure = last || (k !== null && isMaster(best.card, k) && (isTrump(best.card) || !trumpLeft));
    if (secure) {
      // Feed the partner counters when the rules let us.
      const counters = legal.filter((c) => pinochleCounter(c) > 0 && !winners.includes(c));
      if (counters.length > 0) return [...counters].sort((a, b) => pinochleStrength(b) - pinochleStrength(a))[0]!;
    }
    return cheap(legal.filter((c) => !winners.includes(c)).length > 0 ? legal.filter((c) => !winners.includes(c)) : legal);
  }
  if (winners.length > 0) return cheap(winners);
  // Cannot win: shed the least valuable card and keep counters.
  return cheap(legal);
}

export const pinochleBot: BotEngine<PinochleView, PinochleLegal, PinochleAction> = {
  decideAction(view, legal, ctx) {
    switch (legal.kind) {
      case 'bid':
        return this.chooseBet!(view, legal, ctx);
      case 'trump':
        return { type: 'trump', suit: bestTrump(view.hand).suit };
      case 'passCards':
        return { type: 'passCards', cards: choosePass(view, legal.role) };
      case 'play':
        return this.chooseCard!(view, legal, ctx);
      default:
        throw new Error('Bot asked to act without legal actions');
    }
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    const me = view.seat!;
    let est = bestTrump(view.hand).est;
    if (ctx.difficulty === 'BEGINNER') est += (ctx.rng.next() - 0.5) * 12;
    else if (ctx.difficulty === 'EASY') est += (ctx.rng.next() - 0.5) * 7;
    const shade = ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT' ? 1 : 0;
    const ceiling = Math.min(legal.max, Math.floor(est - shade));
    // Leave the bid with a partner who already holds it unless we are clearly stronger.
    if (view.highBid?.seat === pinochlePartnerOf(me) && ceiling < legal.min + 3) return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
    if (ceiling >= legal.min) return { type: 'bid', value: Math.min(ceiling, legal.min + ctx.rng.int(2)) };
    return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 150));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, Math.max(0, (legal.min - bestTrump(view.hand).est + 5) / 40));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
    if (legal.kind === 'trump') return { type: 'trump', suit: bestTrump(view.hand).suit };
    if (legal.kind === 'passCards') {
      const trump = view.trump ?? bestTrump(view.hand).suit;
      const ranked = [...view.hand].sort((a, b) => pinochleCardValue(b, trump) - pinochleCardValue(a, trump));
      const picks = legal.role === 'partner' ? ranked.slice(0, 3) : ranked.slice(-3);
      return { type: 'passCards', cards: [picks[0]!, picks[1]!, picks[2]!] };
    }
    if (legal.kind === 'play') {
      const trump = view.trump!;
      return { type: 'play', card: [...pinochleLegalCards(view.hand, view.trick as PinochleTrickPlay[], trump)].sort((a, b) => pinochleCardValue(a, trump) - pinochleCardValue(b, trump))[0]! };
    }
    throw new Error('No legal action');
  },
};
